import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Target, Users, Award, Zap } from 'lucide-react';
import { Header } from '../components/Header';
import { Footer } from '../components/Footer';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import SEO from '../components/SEO';
import { companyInfo } from '../mockData';

const About = () => {
  return (
    <div className="min-h-screen bg-white">
      <SEO 
        title="About Us"
        description="Learn about Omnia Paratus Talent Solutions - a premier recruiting agency in Boston, MA. Our experienced team specializes in direct hire recruiting for accounting & finance, healthcare, logistics & supply chain, HR & people compliance, and legal sectors nationwide."
        keywords="about Omnia Paratus, recruiting agency Boston, staffing firm Massachusetts, talent acquisition team, executive recruiters, professional staffing services"
      />
      <Header />

      {/* Hero Section */}
      <section className="relative pt-32 pb-20 px-4 sm:px-6 lg:px-8 overflow-hidden">
        <div className="absolute inset-0 z-0">
          <img
            src="https://images.unsplash.com/photo-1758518732175-5d608ba3abdf?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2NjZ8MHwxfHNlYXJjaHwxfHxwcm9mZXNzaW9uYWwlMjBidXNpbmVzcyUyMHRlYW0lMjBvZmZpY2UlMjBjb2xsYWJvcmF0aW9ufGVufDB8fHx8MTc3MTE3OTgyM3ww&ixlib=rb-4.1.0&q=85"
            alt="Team collaboration"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-[#0A1F44]/95 to-[#0A1F44]/80"></div>
        </div>
        <div className="relative z-10 max-w-7xl mx-auto text-center">
          <h1 className="text-5xl md:text-6xl font-bold text-white">
            About Us
          </h1>
        </div>
      </section>

      {/* Mission Section */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-white">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            {/* Image Left */}
            <div className="relative">
              <div className="rounded-2xl overflow-hidden shadow-2xl">
                <img
                  src="https://images.unsplash.com/photo-1758691737207-e75821e080cb?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2Njl8MHwxfHNlYXJjaHwzfHxjYW5kaWQlMjBjb252ZXJzYXRpb24lMjB0d28lMjBwcm9mZXNzaW9uYWxzJTIwbW9kZXJuJTIwb2ZmaWNlJTIwbmF0dXJhbCUyMGxpZ2h0fGVufDB8fHx8MTc3MTE4MDcwNXww&ixlib=rb-4.1.0&q=85"
                  alt="Candid conversation between two professionals"
                  className="w-full h-[420px] object-cover"
                />
              </div>
              <div className="absolute -bottom-4 -left-4 w-32 h-32 bg-[#D4AF37]/10 rounded-2xl -z-10"></div>
              <div className="absolute -top-4 -right-4 w-24 h-24 bg-[#0A1F44]/10 rounded-2xl -z-10"></div>
            </div>

            {/* Content Right */}
            <div>
              <div className="inline-block px-4 py-2 bg-[#D4AF37]/10 rounded-full mb-4">
                <span className="text-[#D4AF37] font-semibold text-sm uppercase tracking-wider">Our Mission</span>
              </div>
              <h2 className="text-4xl md:text-5xl font-bold text-[#0A1F44] mb-8">
                Why We Exist
              </h2>
              <div className="text-lg text-gray-700 leading-relaxed space-y-6">
                <p>
                  Our mission is to make hiring more thoughtful, strategic, and human. We partner closely with organizations to understand not only the skills they need, but the people and culture behind every role — helping build teams that are positioned to grow and succeed.
                </p>
                <p>
                  At the same time, we are committed to guiding professionals toward opportunities where they feel valued, supported, and inspired to thrive. Through integrity, transparency, and genuine partnership, we strive to create meaningful connections that deliver lasting impact for both clients and candidates.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Story Section */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-gray-50">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <div className="inline-block px-4 py-2 bg-[#D4AF37]/10 rounded-full mb-4">
                <span className="text-[#D4AF37] font-semibold text-sm uppercase tracking-wider">Our Story</span>
              </div>
              <h2 className="text-4xl md:text-5xl font-bold text-[#0A1F44] mb-6">
                Built with Vision
              </h2>
              <p className="text-lg text-gray-700 leading-relaxed mb-6">
                Omnia Paratus Talent Solutions was founded on the belief that recruitment should be intentional, strategic, and built on genuine partnership — not transactional placements. With deep experience across talent acquisition and workforce strategy, we created this firm to deliver a more thoughtful and personalized approach to hiring.
              </p>
              <p className="text-lg text-gray-700 leading-relaxed mb-6">
                We work closely with our clients to understand their business goals, operational challenges, and long-term workforce plans, while also taking the time to learn what truly drives our candidates. This dual perspective allows us to create placements that are not only technically strong, but sustainable and aligned for long-term success.
              </p>
              <p className="text-lg text-gray-700 leading-relaxed">
                Today, Omnia Paratus Talent Solutions supports organizations across the United States with specialized recruitment in accounting & finance, healthcare, logistics & supply chain, HR & people compliance, and legal sectors. While industries may vary, our philosophy remains constant: strategic insight, personalized service, and a commitment to quality over volume.
              </p>
            </div>
            <div className="relative">
              <div className="rounded-2xl overflow-hidden shadow-2xl">
                <img
                  src="https://images.unsplash.com/photo-1758518729685-f88df7890776?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2NjZ8MHwxfHNlYXJjaHwzfHxwcm9mZXNzaW9uYWwlMjBidXNpbmVzcyUyMHRlYW0lMjBvZmZpY2UlMjBjb2xsYWJvcmF0aW9ufGVufDB8fHx8MTc3MTE3OTgyM3ww&ixlib=rb-4.1.0&q=85"
                  alt="Professional team meeting"
                  className="w-full h-[420px] object-cover"
                />
              </div>
              {/* Decorative accent */}
              <div className="absolute -bottom-4 -right-4 w-32 h-32 bg-[#D4AF37]/10 rounded-2xl -z-10"></div>
              <div className="absolute -top-4 -left-4 w-24 h-24 bg-[#0A1F44]/10 rounded-2xl -z-10"></div>
            </div>
          </div>
        </div>
      </section>

      {/* Values Section */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-white">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <div className="inline-block px-4 py-2 bg-[#D4AF37]/10 rounded-full mb-4">
              <span className="text-[#D4AF37] font-semibold text-sm uppercase tracking-wider">Our Values</span>
            </div>
            <h2 className="text-4xl md:text-5xl font-bold text-[#0A1F44] mb-4">
              Guiding Principles
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {companyInfo.values.map((value, index) => (
              <Card key={index} className="group border-2 border-gray-200 hover:border-[#D4AF37] transition-all duration-300 hover:shadow-xl">
                <CardContent className="p-8">
                  <div className="flex items-start space-x-4">
                    <div className="flex-shrink-0 w-16 h-16 bg-[#0A1F44] rounded-xl flex items-center justify-center group-hover:bg-[#D4AF37] transition-colors duration-300">
                      <span className="text-2xl font-bold text-white">{index + 1}</span>
                    </div>
                    <div>
                      <h3 className="text-2xl font-bold text-[#0A1F44] mb-3">{value.name}</h3>
                      <p className="text-gray-600 leading-relaxed">{value.description}</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Team Section */}
      <section className="relative py-20 px-4 sm:px-6 lg:px-8 overflow-hidden">
        <div className="absolute inset-0 z-0">
          <img
            src="https://images.unsplash.com/photo-1758518730178-6e237bc8b87d?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2NjZ8MHwxfHNlYXJjaHw0fHxwcm9mZXNzaW9uYWwlMjBidXNpbmVzcyUyMHRlYW0lMjBvZmZpY2UlMjBjb2xsYWJvcmF0aW9ufGVufDB8fHx8MTc3MTE3OTgyM3ww&ixlib=rb-4.1.0&q=85"
            alt="Business collaboration"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-[#0A1F44]/90"></div>
        </div>
        <div className="relative z-10 max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-white mb-4">
              Experience That Delivers
            </h2>
            <p className="text-lg text-gray-300 max-w-2xl mx-auto">
              Our team brings decades of combined experience in talent acquisition, 
              recruiting, and human resources across diverse industries.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {[
              { number: '50+', label: 'Years Combined Experience' },
              { number: '200+', label: 'Companies Served' },
              { number: '5', label: 'Core Industry Specialties' }
            ].map((stat, index) => (
              <Card key={index} className="border-none shadow-lg text-center bg-white/10 backdrop-blur-sm">
                <CardContent className="p-10">
                  <div className="text-6xl font-bold text-[#D4AF37] mb-3">{stat.number}</div>
                  <div className="text-lg text-white font-medium">{stat.label}</div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-gradient-to-br from-[#0A1F44] to-[#0d2b5c]">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-4xl md:text-5xl font-bold text-white mb-6">
            Let's Work Together
          </h2>
          <p className="text-xl text-gray-200 mb-10">
            Experience the difference that personalized, strategic recruiting can make for your organization.
          </p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
            <Button
              asChild
              size="lg"
              className="bg-[#D4AF37] text-[#0A1F44] hover:bg-[#D4AF37]/90 text-lg px-8 py-6 shadow-xl"
            >
              <Link to="/contact" className="flex items-center gap-2">
                Get In Touch <ArrowRight className="h-5 w-5" />
              </Link>
            </Button>
            <Button
              asChild
              size="lg"
              variant="outline"
              className="bg-white/10 backdrop-blur-sm border-white/30 text-white hover:bg-white hover:text-[#0A1F44] text-lg px-8 py-6"
            >
              <Link to="/services">Our Services</Link>
            </Button>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default About;
